import { Today } from '@mui/icons-material'
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import ListItemText from '@mui/material/ListItemText'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import I18n from 'i18n-js'
import moment from 'moment'
import { memo } from 'react'
import { makeStyles } from 'tss-react/mui'

import LinkChild from '@src/lib/LinkChild'

import cloudinary from '../../lib/Cloudinary'

import PlatformAvatarGroup from './PlatformAvatarGroup'
import reportingPaths from './reportingPaths'
import StatRow from './StatRow'
import { campaignTotals, channelTotals } from './statsTotals'

const potentialChannels = [
  { name: 'facebook', type: 'views' },
  { name: 'twitter', type: 'clicks' },
  { name: 'email', type: 'views' },
  { name: 'instagram', type: 'views' },
]

const useStyles = makeStyles()((theme) => ({
  campaignImage: {
    width: 100,
    height: 56,
    objectFit: 'contain',
    marginRight: theme.spacing(2),
  },
}))

const campaignState = (state) => {
  if (state === 'expired') return I18n.t('reporting.completed')
  else {
    return state
  }
}

const Channels = (props) => {
  if (props.channels.length > 0) {
    return <PlatformAvatarGroup platforms={props.channels} />
  } else if (!props.channels.length && !props.allSharesAreScheduled) {
    return (
      <Box display="flex" color="text.secondary" fontStyle="italic">
        <HourglassEmptyRoundedIcon />
        <Typography>{I18n.t('reporting.waitingForStats')}</Typography>
      </Box>
    )
  } else if (!props.channels.length && props.allSharesAreScheduled) {
    return (
      <Box display="flex" color="text.secondary" fontStyle="italic">
        <Today />
        <Typography>{I18n.t('reporting.sharesScheduled')}</Typography>
      </Box>
    )
  }
  return
}

/** @type {React.FC<{campaign: import('@promoboxx/graphql-gateway-types').Campaign, params: {retailerAccountSlug: string, brandSlug: string, campaignType: string} => void}>} */
const OrganicCampaignListItem = (props) => {
  const { classes } = useStyles()

  // TODO Yeah some premium ads have an invalid brand id
  if (!props.campaign.brand) {
    return null
  }

  const stats = props.campaign.getStats.stats
  const shares =
    (stats && stats[0] && stats[0].data && stats[0].data.shares) || []
  const { totals, itemized } = campaignTotals(shares)

  const channels = potentialChannels.reduce((activeChannels, channel) => {
    return channelTotals({
      channel: channel.name,
      stats: shares,
      type: channel.type,
    })
      ? [...activeChannels, channel.name]
      : activeChannels
  }, [])

  return (
    <TableRow key={props.campaign.id}>
      <TableCell>
        <Box alignItems="center" display="flex">
          <img
            alt={props.campaign.name}
            className={classes.campaignImage}
            src={cloudinary.instance().url(props.campaign.logo_public_id, {
              crop: 'scale',
              width: 100,
              format: 'jpg',
              quality: 'auto',
            })}
          />
          <ListItemText
            primary={props.campaign.name}
            secondary={props.campaign.brand.name}
          />
        </Box>
      </TableCell>
      <TableCell>
        <Channels
          channels={channels}
          allSharesAreScheduled={props.campaign.getShares.data.every(
            (share) => share.scheduled_for && share.executed_at === null,
          )}
        />
      </TableCell>
      <TableCell>
        {props.campaign.start_date &&
          moment(props.campaign.start_date).format('L')}{' '}
        -<br />
        {props.campaign.end_date && moment(props.campaign.end_date).format('L')}
      </TableCell>
      <TableCell>
        {props.campaign.state && (
          <Chip
            color="secondary"
            label={campaignState(props.campaign.state).toUpperCase()}
            size="small"
            variant="outlined"
          />
        )}
      </TableCell>
      <TableCell align="center">
        <Tooltip
          title={
            <>
              <StatRow
                label={I18n.t('reporting.organic')}
                value={itemized.views.impressions_organic || 0}
              />
              <StatRow
                label={I18n.t('reporting.paid')}
                value={itemized.views.impressions_paid || 0}
              />
            </>
          }
        >
          <span>{totals.views != null ? totals.views : '-'}</span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Tooltip
          title={
            <>
              <StatRow
                label={I18n.t('reporting.likes')}
                value={itemized.actions.post_reaction_like || 0}
              />
              <StatRow
                label={I18n.t('reporting.comments')}
                value={itemized.actions.comments || 0}
              />
              <StatRow
                label={I18n.t('reporting.shares')}
                value={itemized.actions.posts || 0}
              />
            </>
          }
        >
          <span>{totals.actions != null ? totals.actions : '-'}</span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        {totals.clicks != null ? totals.clicks : '-'}
      </TableCell>
      <TableCell>
        <LinkChild
          to={reportingPaths.organicCampaignDetails.build({
            retailerAccountSlug: props.params.retailerAccountSlug,
            campaignId: props.campaign.creative_path,
          })}
        >
          <Button fullWidth>{I18n.t('shared.view')}</Button>
        </LinkChild>
      </TableCell>
    </TableRow>
  )
}

export default memo(OrganicCampaignListItem)
